import { useState } from 'react';
import { Task } from '@/hooks/useTasks';
import { Badge } from '@/components/ui/badge';
import { Calendar, Mail, AlertCircle, User, FolderOpen, GripVertical, Paperclip } from 'lucide-react';
import { format } from 'date-fns';
import { useDraggable } from '@dnd-kit/core';
import { TaskEmailsDialog } from './TaskEmailsDialog';
import { useTaskAttachmentCount } from '@/hooks/useTaskAttachmentCount';

interface TaskCardProps {
  task: Task;
}

export function TaskCard({ task }: TaskCardProps) {
  const [emailsDialogOpen, setEmailsDialogOpen] = useState(false);
  const { data: attachmentCount } = useTaskAttachmentCount(task.id);
  
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: task.id,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1,
      }
    : undefined;

  const priorityColors = {
    critical: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-yellow-500',
    low: 'bg-gray-500',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-card rounded-lg border p-3 shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Drag handle with priority indicator */}
      <div 
        {...listeners}
        {...attributes}
        className="flex items-center gap-2 mb-2 cursor-grab active:cursor-grabbing"
      >
        <div className={`h-1 flex-1 rounded-lg ${priorityColors[task.priority]}`} />
        <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      </div>

      {/* Title */}
      <h3 className="font-medium text-sm mb-2 line-clamp-2">{task.title}</h3>

      {/* AI Badge */}
      {task.created_by_ai && (
        <Badge variant="secondary" className="text-xs mb-2">
          ✨ AI-extracted
        </Badge>
      )}

      {/* Project Badge */}
      {task.project && (
        <Badge variant="outline" className="text-xs mb-2">
          <FolderOpen className="h-3 w-3 mr-1" />
          {task.project.name}
        </Badge>
      )}

      {/* Metadata */}
      <div className="space-y-1 text-xs text-muted-foreground">
        {task.due_date && (
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {format(new Date(task.due_date), 'MMM d')}
            {task.urgency_score && task.urgency_score > 0.7 && (
              <AlertCircle className="h-3 w-3 text-red-500" />
            )}
          </div>
        )}
        
        {task.assigned_to_name && (
          <div className="flex items-center gap-1">
            <User className="h-3 w-3" />
            {task.assigned_to_name}
          </div>
        )}

        {(task.linked_emails_count ?? 0) > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEmailsDialogOpen(true);
            }}
            className="flex items-center gap-1 hover:text-primary transition-colors cursor-pointer"
          >
            <Mail className="h-3 w-3" />
            {task.linked_emails_count} email{task.linked_emails_count > 1 ? 's' : ''}
          </button>
        )}

        {(attachmentCount ?? 0) > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEmailsDialogOpen(true);
            }}
            className="flex items-center gap-1 hover:text-primary transition-colors cursor-pointer"
          >
            <Paperclip className="h-3 w-3" />
            {attachmentCount} attachment{attachmentCount > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {task.tags.slice(0, 3).map(tag => (
            <Badge key={tag} variant="outline" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      <TaskEmailsDialog
        open={emailsDialogOpen}
        onOpenChange={setEmailsDialogOpen}
        taskId={task.id}
      />
    </div>
  );
}
